<script setup lang="ts">

import { defineProps, computed } from 'vue'
import { useAssetStore } from '@/stores/assetStore.ts'
import MetricDisplay from '@/components/Elements/Generic/MetricDisplay.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { usePlayerRelationStore } from '@/stores/playerRelations.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { useI18n } from 'vue-i18n'
import StyledImage from '@/components/Elements/Images/StyledImage.vue'

const { t } = useI18n()

/**
 * Props
 */
const props = defineProps({
  spy: {
    type: Object,
    required: true
  },
  type: {
    type: String,
    required: true
  }
});

/**
 * Stores
 */
const assetStore = useAssetStore();
const gameStore = useGameStore();
const playerRelationStore = usePlayerRelationStore();

/**
 * Computed
 */
const mission = computed(() => assetStore.findSpyMissionByCode(props.spy.mission)!);

const image = computed(() => {
  if(!props.spy.targetPlayerId) return gameStore.player?.profileImage
  return playerRelationStore.getProfileImageByPlayerId(props.spy.targetPlayerId)
})

const playerName = computed(() => {
  if(!props.spy.targetPlayerId) return gameStore.player?.nickName
  return playerRelationStore.getNameByPlayerId(props.spy.targetPlayerId)
})


</script>

<template>
  <div v-if="type==='detailed'">
    <div class="flex flex-col">
      <div class="text-metal-300 pb-1">{{ t(`diplomacy.spies.missions.${props.spy.mission}.name`) }}</div>
      <div> {{ t(`diplomacy.spies.missions.${props.spy.mission}.description`) }} </div>
      <div>
        <div class="pt-2" style="width: 225px; height: 225px;" :title="playerName">
          <StyledImage :src="image ?? ''" :alt="playerName" size="full" />
        </div>
        <div class="text-center w-full pt-2">{{ playerName }}</div>
      </div>
      <MetricDisplay :text="t('diplomacy.spies.missions.difficulty')" class="w-full mt-2">
        <GenericIcon
          v-for="n in mission.difficulty"
          :key="n"
          type="spy"
          :tooltip="t('diplomacy.spies.tooltips.difficulty')"
          class="ms-1"
        />
      </MetricDisplay>
      <MetricDisplay v-if="mission.cost>0" :text="t('diplomacy.spies.missions.cost')" class="w-full mt-2">
        <GenericIcon
          v-for="n in mission.cost"
          :key="n"
          type="credit"
          :tooltip="t('diplomacy.spies.tooltips.cost')"
          class="ms-1"
        />
      </MetricDisplay>
    </div>
  </div>
  <div v-else-if="type==='basic'">
    <div class="flex justify-between p-1 w-full bg-metal-900 rounded">
      <div class="w-10 h-10 shrink-0" :title="playerName">
        <StyledImage :src="image ?? ''" :alt="playerName" size="full" />
      </div>
      <div class="flex flex-grow flex-col justify-center text-center">
        <span v-if="spy.killed" class="text-white">{{ t('diplomacy.spies.missions.killed') }}</span>
        <span v-else class="text-cyan-500">{{ t(`diplomacy.spies.missions.${props.spy.mission}.name`) }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

</style>